import { PAGES } from "@dune/types";
import type { SubmissionParticipantForReception } from "@dune/types";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	Button,
	H2,
	Separator,
} from "@dune/ui";
import { formatCurrency } from "@dune/utils";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	useDeleteReceptionParticipant,
	useValidateReception,
} from "../../../../api/mutations/constructionSiteSubmission.mutations";
import {
	useGetConstructionSiteReception,
	useGetConstructionSiteSubmissionOperation,
} from "../../../../api/queries/constructionSiteSubmission.queries";
import { BuildersTableReception } from "../../../../component/submission-participants/BuildersTableReception";
import { DesignersTableReception } from "../../../../component/submission-participants/DesignersTableReception";
import { MaterialsTableReception } from "../../../../component/submission-participants/MaterialsTableReception";
import { useShouldDisplayHTAmount } from "../../../../hooks/useParticipantToSubmissionForm";
import { useReceptionParticipantsView } from "../../../../hooks/useReceptionParticipantsView";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import { useQueryClient } from "@tanstack/react-query";
import { constructionSiteSubmissionQueryKeys } from "../../../../api/query-keys/constructionSiteSubmission.query-keys";

type Props = { onPrev: () => void };

export const ReceptionStep3Participants = ({ onPrev }: Props) => {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const navigate = useNavigate();

	const queryClient = useQueryClient();

	const [selectedParticipantId, setSelectedParticipantId] = useState<
		number | null
	>(null);

	const numericId = useMemo(
		() => Number(constructionSiteId),
		[constructionSiteId],
	);
	const { data: receptionData } = useGetConstructionSiteReception(numericId);
	const { mutate: validateReception, isPending: isSubmitting } =
		useValidateReception();

	const { designers, builders, materials } = useReceptionParticipantsView(
		Number(constructionSiteId),
	);

	const { data: submissionWithOperationDetails } =
		useGetConstructionSiteSubmissionOperation(Number(constructionSiteId));

	const { shouldDisplayHT } = useShouldDisplayHTAmount(
		Number(constructionSiteId),
	);

	const totalAmountDeclared = useMemo(() => {
		const sum = (list: SubmissionParticipantForReception[]) =>
			list.reduce((acc, p) => {
				const amount = shouldDisplayHT ? p.totalFeesHT : p.totalFeesTTC;
				return acc + (amount || 0);
			}, 0);
		return sum(designers) + sum(builders) + sum(materials);
	}, [designers, builders, materials, shouldDisplayHT]);

	const constructionCost = useMemo(() => {
		const totalAmount = shouldDisplayHT
			? submissionWithOperationDetails?.constructionSite
					?.constructionSiteDetails?.totalAmountHT
			: submissionWithOperationDetails?.constructionSite
					?.constructionSiteDetails?.totalAmountTTC;
		return totalAmount || 0;
	}, [submissionWithOperationDetails, shouldDisplayHT]);

	const handleParticipantSelect = (
		participantId: number,
		isSelected: boolean,
	) => {
		if (isSelected) setSelectedParticipantId(participantId);
		else setSelectedParticipantId(null);
	};

	const { mutate: deleteReceptionParticipant } = useDeleteReceptionParticipant(
		Number(constructionSiteId),
	);

	const handleSubmit = async () => {
		if (!receptionData) return;
		validateReception(
			{
				constructionSiteId: Number(constructionSiteId),
				data: {
					receptionDate: receptionData.receptionDate || undefined,
					hasAuthorizationChange:
						receptionData.hasAuthorizationChange || undefined,
				},
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: constructionSiteSubmissionQueryKeys.participantsReception(
							Number(constructionSiteId),
						),
					});
					navigate(PAGES.PROJECTS.show.withParams(constructionSiteId));
				},
				onError: (error: unknown) => {
					console.log({ error });
				},
			},
		);
	};
	const handleDeleteParticipant = () => {
		if (selectedParticipantId) {
			deleteReceptionParticipant(selectedParticipantId, {
				onSuccess: () => {
					setSelectedParticipantId(null);
				},
			});
		}
	};

	const handleEditParticipant = () => {
		if (selectedParticipantId) {
			const all = [...designers, ...builders, ...materials];
			const participant = all.find(
				(p: SubmissionParticipantForReception) =>
					p.participant.id === selectedParticipantId,
			);
			if (participant) {
				if (participant.type === "DESIGNER") {
					navigate(
						PAGES.PROJECTS.reception.participants.editDesignerWithParams(
							constructionSiteId,
							selectedParticipantId.toString(),
						),
					);
				} else if (participant.type === "BUILDER") {
					navigate(
						PAGES.PROJECTS.reception.participants.editBuilderWithParams(
							constructionSiteId,
							selectedParticipantId.toString(),
						),
					);
				} else if (participant.type === "MATERIAL") {
					navigate(
						PAGES.PROJECTS.reception.participants.editMaterialWithParams(
							constructionSiteId,
							selectedParticipantId.toString(),
						),
					);
				}
			}
		}
	};

	const handleAddDesigner = () => {
		navigate(
			PAGES.PROJECTS.reception.participants.addDesignerWithParams(
				constructionSiteId,
			),
		);
	};

	const handleAddBuilder = () => {
		navigate(
			PAGES.PROJECTS.reception.participants.addBuilderWithParams(
				constructionSiteId,
			),
		);
	};

	const handleAddMaterial = () => {
		navigate(
			PAGES.PROJECTS.reception.participants.addMaterialWithParams(
				constructionSiteId,
			),
		);
	};

	return (
		<div>
			<div className="flex justify-between items-start mt-4 mb-8">
				<H2>Intervenants</H2>
				<div className="flex gap-2">
					<Button
						variant="outline"
						disabled={!selectedParticipantId}
						onClick={handleEditParticipant}
					>
						Modifier
					</Button>
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button variant="destructive" disabled={!selectedParticipantId}>
								Supprimer
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
								<AlertDialogDescription>
									Êtes-vous sûr de vouloir supprimer ce participant de la
									soumission ? Cette action est irréversible.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Annuler</AlertDialogCancel>
								<AlertDialogAction onClick={handleDeleteParticipant}>
									Supprimer
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>

			<div className="space-y-6">
				<div>
					<DesignersTableReception
						submissionParticipants={designers}
						constructionSiteId={Number(constructionSiteId)}
						selectedParticipantId={selectedParticipantId}
						onParticipantSelect={handleParticipantSelect}
					/>
					<Button onClick={handleAddDesigner} className="h-12 mt-2">
						<PlusIcon /> Ajouter un concepteur
					</Button>
				</div>

				<Separator />

				<div>
					<BuildersTableReception
						submissionParticipants={builders}
						constructionSiteId={Number(constructionSiteId)}
						selectedParticipantId={selectedParticipantId}
						onParticipantSelect={handleParticipantSelect}
					/>
					<Button onClick={handleAddBuilder} className="h-12 mt-2">
						<PlusIcon /> Ajouter un réalisateur
					</Button>
				</div>

				<Separator />

				<div>
					<MaterialsTableReception
						submissionParticipants={materials}
						constructionSiteId={Number(constructionSiteId)}
						selectedParticipantId={selectedParticipantId}
						onParticipantSelect={handleParticipantSelect}
					/>
					<Button onClick={handleAddMaterial} className="h-12 mt-2">
						<PlusIcon /> Ajouter un matériau
					</Button>
				</div>
			</div>

			<Separator className="mt-8" />

			<div className="mt-8">
				<div className="text-right text-base font-semibold text-text-main-blue mb-4 flex gap-3 justify-end">
					<div>TOTAL DES MONTANTS DÉCLARÉS</div>
					<div>
						{formatCurrency(totalAmountDeclared)}{" "}
						{shouldDisplayHT ? "HT" : "TTC"}
					</div>
				</div>
				<div className="text-right text-sm text-text-secondary-blue flex ml-auto gap-3 justify-end">
					<div>Coût de construction prévisionnel déclaré</div>
					<div>
						{formatCurrency(constructionCost)} {shouldDisplayHT ? "HT" : "TTC"}
					</div>
				</div>
			</div>

			<div className="flex justify-between mt-8">
				<Button variant="outline" onClick={onPrev}>
					Précédent
				</Button>
				<div className="flex gap-2">
					<Button
						onClick={handleSubmit}
						disabled={!receptionData || isSubmitting}
						variant="default"
						isLoading={isSubmitting}
					>
						Valider la réception du chantier
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ReceptionStep3Participants;
